import { useEffect } from 'react';
import { Alert, App, AutoComplete, Button, Drawer, Form, Input, Popconfirm, Switch, Tag } from 'antd';
import { DataTable } from '../../components/DataTable';
import type { ColumnsType } from 'antd/es/table';
import { Info, WarningCircle } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ErrorState, LoadingState } from '../../components/PageStates';
import { ApiError, NetworkError } from '../../api/client';
import { catalogApi, catalogKeys, type BankAccount, type Branch } from './api';
import { BANK_OPTIONS, bankLabel } from './banks';
import { tokens } from '../../theme';

interface FormValues {
  bankBin: string;
  accountNo: string;
  accountName: string;
}

const BANK_AUTOCOMPLETE_OPTIONS = BANK_OPTIONS.map((bank) => ({
  value: bank.bin,
  label: `${bank.bin} — ${bank.shortName}`,
}));

export function BankAccountsDrawer({ branch, onClose }: { branch: Branch | null; onClose: () => void }) {
  const [form] = Form.useForm<FormValues>();
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  const open = branch !== null;
  const branchId = branch?.id ?? '';

  useEffect(() => {
    if (open) form.resetFields();
  }, [open, form]);

  const accountsQuery = useQuery({
    queryKey: catalogKeys.bankAccounts(branchId),
    queryFn: () => catalogApi.listBankAccounts(branchId),
    enabled: open,
  });

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: catalogKeys.bankAccounts(branchId) });
    // Danh sách chi nhánh mang theo activeBankAccountCount — nó quyết định dòng
    // cảnh báo màu hổ phách, nên phải làm mới cùng lúc
    await queryClient.invalidateQueries({ queryKey: catalogKeys.branches });
  };

  const addMutation = useMutation({
    mutationFn: (values: FormValues) => catalogApi.addBankAccount(branchId, values),
    onSuccess: async () => {
      await invalidate();
      form.resetFields();
      message.success('Đã thêm tài khoản nhận tiền.');
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      catalogApi.setBankAccountActive(id, isActive),
    onSuccess: async (account) => {
      await invalidate();
      message.success(account.isActive ? 'Đã bật lại tài khoản.' : 'Đã tắt tài khoản.');
    },
    onError: (error) => {
      message.error(
        error instanceof ApiError || error instanceof NetworkError
          ? error.message
          : 'Không đổi được trạng thái tài khoản.',
      );
    },
  });

  const accounts = accountsQuery.data ?? [];
  const activeCount = accounts.filter((x) => x.isActive).length;

  const columns: ColumnsType<BankAccount> = [
    {
      title: 'Ngân hàng',
      key: 'bank',
      render: (_, account) => (
        <>
          <div style={{ fontWeight: 600 }}>{bankLabel(account.bankBin)}</div>
          <div style={{ fontSize: 11.5, color: tokens.color.ink3, fontFamily: 'monospace' }}>
            {account.bankBin}
          </div>
        </>
      ),
    },
    {
      title: 'Số tài khoản',
      key: 'accountNo',
      render: (_, account) => (
        <>
          <div style={{ fontFamily: 'monospace' }}>{account.accountNo}</div>
          <div style={{ fontSize: 11.5, color: tokens.color.ink3 }}>{account.accountName}</div>
        </>
      ),
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 150,
      render: (_, account) => (
        <Popconfirm
          title={account.isActive ? 'Tắt tài khoản này?' : 'Bật lại tài khoản này?'}
          description={
            account.isActive
              ? 'Hóa đơn chuyển vào tài khoản này sẽ không tích điểm được nữa.'
              : 'Hóa đơn chuyển vào tài khoản này sẽ tích điểm được trở lại.'
          }
          okText="Đồng ý"
          cancelText="Hủy"
          onConfirm={() => toggleMutation.mutate({ id: account.id, isActive: !account.isActive })}
        >
          <Switch
            checked={account.isActive}
            loading={toggleMutation.isPending && toggleMutation.variables?.id === account.id}
            checkedChildren="Đang dùng"
            unCheckedChildren="Đã tắt"
          />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Drawer
      open={open}
      width={560}
      onClose={onClose}
      title={branch === null ? '' : `Tài khoản nhận tiền — ${branch.name}`}
      destroyOnHidden
    >
      {activeCount === 0 ? (
        <Alert
          style={{ marginBottom: tokens.space.sectionGap }}
          type="warning"
          showIcon
          icon={<WarningCircle size={20} weight="fill" aria-hidden />}
          message="Chi nhánh này chưa quét hóa đơn được"
          description="Hệ thống chỉ tích điểm cho hóa đơn chuyển vào tài khoản đã khai ở đây. Chưa có tài khoản nào đang dùng thì mọi lần quét đều bị từ chối."
        />
      ) : (
        <Alert
          style={{ marginBottom: tokens.space.sectionGap }}
          type="info"
          showIcon
          icon={<Info size={20} aria-hidden />}
          message={`${activeCount} tài khoản đang dùng`}
          description="Một tài khoản có thể khai ở nhiều chi nhánh — chuỗi F&B thường dùng chung tài khoản thu."
        />
      )}

      {accountsQuery.isPending ? (
        <LoadingState rows={3} />
      ) : accountsQuery.isError ? (
        <ErrorState error={accountsQuery.error} onRetry={() => void accountsQuery.refetch()} />
      ) : (
        <DataTable toolbar={false}
          rowKey="id"
          columns={columns}
          dataSource={accounts}
          pagination={false}
          size="small"
          locale={{ emptyText: 'Chưa khai tài khoản nào.' }}
        />
      )}

      <div
        style={{
          marginTop: tokens.space.sectionGap,
          paddingTop: tokens.space.sectionGap,
          borderTop: `1px solid ${tokens.color.line}`,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 12 }}>Thêm tài khoản</div>

        {addMutation.isError && (
          <Alert
            style={{ marginBottom: 16 }}
            type="error"
            showIcon
            message={
              addMutation.error instanceof ApiError || addMutation.error instanceof NetworkError
                ? addMutation.error.message
                : 'Có lỗi không xác định. Thử lại hoặc báo kỹ thuật.'
            }
          />
        )}

        <Form<FormValues>
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={(values) => addMutation.mutate(values)}
        >
          <Form.Item
            label="Mã ngân hàng"
            name="bankBin"
            // Danh sách gợi ý không đầy đủ nên vẫn cho gõ tay 6 số bất kỳ.
            // Cách chắc ăn nhất là đối chiếu với chính mã QR in trên bill.
            tooltip="6 chữ số theo chuẩn NAPAS. Đối chiếu với mã QR in trên bill cho chắc."
            rules={[
              { required: true, message: 'Chưa nhập mã ngân hàng.' },
              { pattern: /^\d{6}$/, message: 'Mã ngân hàng gồm đúng 6 chữ số (chuẩn NAPAS).' },
            ]}
          >
            <AutoComplete
              options={BANK_AUTOCOMPLETE_OPTIONS}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              <Input maxLength={6} inputMode="numeric" />
            </AutoComplete>
          </Form.Item>

          <Form.Item
            label="Số tài khoản"
            name="accountNo"
            rules={[{ required: true, message: 'Chưa nhập số tài khoản.' }]}
          >
            <Input maxLength={30} style={{ fontFamily: 'monospace' }} />
          </Form.Item>

          <Form.Item
            label="Tên chủ tài khoản"
            name="accountName"
            rules={[{ required: true, message: 'Chưa nhập tên chủ tài khoản.' }]}
          >
            <Input maxLength={200} />
          </Form.Item>

          <Button type="primary" htmlType="submit" loading={addMutation.isPending}>
            Thêm tài khoản
          </Button>
        </Form>
      </div>

      {accounts.length > 0 && activeCount < accounts.length && (
        <Tag style={{ marginTop: 16 }} color="default">
          {accounts.length - activeCount} tài khoản đã tắt vẫn giữ lại để đối soát hóa đơn cũ
        </Tag>
      )}
    </Drawer>
  );
}
